import ReduxQuerySync from 'redux-query-sync';
import moment from 'moment';

import { store } from './configureStore';
import {
  SEARCH_SET_CRITERIA,
  SEARCH_SET_DOCUMENTS_PER_PAGE,
  SEARCH_SET_MAX_DATE,
  SEARCH_SET_MIN_DATE,
  SEARCH_SET_PAGE
} from './reducers/search';
import history from './history';
import { UI_SET_LANG } from './reducers/ui';

ReduxQuerySync({
  store,
  params: {
    q: {
      selector: state => state.search.query,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          query: value
        }
      })
    },
    mm: {
      selector: state => state.search.minimumMatch,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          minimumMatch: value
        }
      })
    },
    adv: {
      selector: state => state.search.adv,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          adv: value
        }
      }),
      defaultValue: ''
    },
    dp: {
      selector: state => state.search.pagination.documentsPerPage,
      action: value => ({
        type: SEARCH_SET_DOCUMENTS_PER_PAGE,
        payload: {
          documentsPerPage: value
        }
      }),
      stringToValue: string => parseInt(string, 0) || 30,
      valueToString: value => `${value}`,
      defaultValue: 30
    },
    p: {
      selector: state => state.search.pagination.currentPage,
      action: value => ({
        type: SEARCH_SET_PAGE,
        payload: value
      }),
      stringToValue: string => parseInt(string, 0) || 1,
      valueToString: value => `${value}`,
      defaultValue: 1
    },
    st: {
      selector: state => state.search.sort,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          sort: value
        }
      }),
      defaultValue: ''
    },
    pf: {
      selector: state => state.search.platforms,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          platforms: value
        }
      }),
      stringToValue: string => {
        const array = string.split(',');

        return {
          OJ: array.includes('OJ'),
          OB: array.includes('OB'),
          HO: array.includes('HO'),
          CO: array.includes('CO')
        };
      },
      valueToString: value => {
        let arrayData = [];

        Object.keys(value).forEach(key => {
          if (value[key]) arrayData.push(key);
        });

        return arrayData.join(',');
      },
      defaultValue: ''
    },
    a: {
      selector: state =>
        state.search.access.length === 0 ? '1' : state.search.access,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          access: value === '1' ? [] : value
        }
      }),
      stringToValue: string => (string ? string.split(',') : '1'),
      valueToString: value => (value.length === '1' ? '1' : value.join(',')),
      defaultValue: '1'
    },
    lang: {
      selector: state =>
        state.search.translations.length === 0
          ? '1'
          : state.search.translations,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          translations: value === '1' ? [] : value
        }
      }),
      stringToValue: string => (string ? string.split(',') : '1'),
      valueToString: value => (value.length === '1' ? '1' : value.join(',')),
      defaultValue: '1'
    },
    t: {
      selector: state =>
        state.search.type.length === 0 ? '1' : state.search.type,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          type: value === '1' ? [] : value
        }
      }),
      stringToValue: string => (string ? string.split(',') : '1'),
      valueToString: value => (value.length === '1' ? '1' : value.join(',')),
      defaultValue: '1'
    },
    au: {
      selector: state =>
        state.search.author.length === 0 ? '1' : state.search.author,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          author: value === '1' ? [] : value
        }
      }),
      stringToValue: string => {
        if (!string) return '1';
        // Support both separators: `;` (from UI components) and ` OR ` (legacy)
        const authors = string.includes(';')
          ? string.split(';')
          : string.split(/\s+OR\s+/);
        return authors.map(a => a.trim()).filter(a => a);
      },
      valueToString: value => (Array.isArray(value) ? value.join(';') : value),
      defaultValue: '1'
    },
    s: {
      selector: state => {
        let value = [];

        if (state.search.journalsPublications.length > 0)
          value = state.search.journalsPublications;
        else if (state.search.booksPublications.length > 0)
          value = state.search.booksPublications;
        else if (state.search.hypothesesPublications.length > 0)
          value = state.search.hypothesesPublications;

        return value;
      },
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          journalsPublications: value,
          booksPublications: value,
          hypothesesPublications: value
        }
      }),
      stringToValue: string => (string ? string.split(',') : []),
      valueToString: value => value.join(','),
      defaultValue: []
    },
    fr: {
      selector: state => state.search.years.min,
      action: value => ({
        type: SEARCH_SET_MIN_DATE,
        payload: value
      }),
      stringToValue: string => parseInt(string, 0),
      valueToString: value => `${value}`,
      defaultValue: 1900
    },
    to: {
      selector: state => state.search.years.max,
      action: value => ({
        type: SEARCH_SET_MAX_DATE,
        payload: value
      }),
      stringToValue: string => parseInt(string, 0),
      valueToString: value => `${value}`,
      defaultValue: parseInt(moment().format('YYYY'), 0)
    },
    l: {
      selector: state => state.ui.lang,
      action: value => ({
        type: UI_SET_LANG,
        payload: value
      }),
      defaultValue: navigator.language.split(/[-_]/)[0]
    },
    sub: {
      selector: state =>
        state.search.subscribers.length === 0 ? '1' : state.search.subscribers,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          subscribers: value === '1' ? [] : value
        }
      }),
      stringToValue: string => (string ? string.split(',') : '1'),
      valueToString: value => (value.length === '1' ? '1' : value.join(',')),
      defaultValue: '1'
    },
    th: {
      selector: state =>
        state.search.themes.length === 0 ? '1' : state.search.themes,
      action: value => ({
        type: SEARCH_SET_CRITERIA,
        payload: {
          themes: value === '1' ? [] : value
        }
      }),
      stringToValue: string => (string ? string.split(',') : '1'),
      valueToString: value => (value.length === '1' ? '1' : value.join(',')),
      defaultValue: '1'
    }
  },
  // Initially set the store's state to the current location.
  initialTruth: 'location',
  history
});



// WEBPACK FOOTER //
// ./src/querySyncer.js